import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import { Modal } from "../ui/modal";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../ui/table";

interface OrderStatusManagerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onChange?: () => void; // Trigger refresh in parent
}

export default function OrderStatusManagerModal({ isOpen, onClose, onChange }: OrderStatusManagerModalProps) {
    const [statuses, setStatuses] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchStatuses();
        }
    }, [isOpen]);

    const fetchStatuses = async () => {
        setLoading(true);
        try {
            const res = await client.getApiOrdersStatus() as any;
            const data = res.data?.data || res.data || [];
            setStatuses(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch statuses:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newStatus.trim()) return;

        setCreating(true);
        try {
            await client.postApiOrdersStatusCreate({
                postApiOrdersStatusCreateRequest: {
                    name: newStatus,
                    description: `Status for ${newStatus}`
                }
            });
            setNewStatus('');
            fetchStatuses();
            if (onChange) onChange();
        } catch (err: any) {
            console.error('Failed to create status:', err);
            Swal.fire({
                title: 'Error',
                text: 'Failed to create status: ' + (err?.response?.data?.message || err.message),
                icon: 'error'
            });
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "Are you sure you want to delete this status?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (!result.isConfirmed) return;

        try {
            await client.postApiOrdersStatusDelete({
                postApiOrdersStatusDeleteRequest: { id }
            });
            fetchStatuses();
            if (onChange) onChange();
            fetchStatuses();
            if (onChange) onChange();
        } catch (err: any) {
            console.error('Failed to delete status:', err);
            Swal.fire({
                title: 'Error',
                text: 'Failed to delete status',
                icon: 'error'
            });
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="max-w-2xl">
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Manage Order Statuses</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleCreate} className="flex gap-3 mb-6">
                    <input
                        type="text"
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                        placeholder="New Status Name (e.g. Processing, Shipped)"
                        className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                    />
                    <button
                        type="submit"
                        disabled={creating}
                        className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50 whitespace-nowrap"
                    >
                        {creating ? 'Adding...' : 'Add Status'}
                    </button>
                </form>

                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableCell isHeader className="px-5 py-3 text-start">ID</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-start">Name</TableCell>
                                <TableCell isHeader className="px-5 py-3 text-end">Action</TableCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && statuses.length === 0 ? (
                                <TableRow><TableCell colSpan={3} className="px-5 py-4 text-center">Loading...</TableCell></TableRow>
                            ) : statuses.length === 0 ? (
                                <TableRow><TableCell colSpan={3} className="px-5 py-4 text-center">No statuses found. Add one above.</TableCell></TableRow>
                            ) : (
                                statuses.map((status) => (
                                    <TableRow key={status.id}>
                                        <TableCell className="px-5 py-3">#{status.id}</TableCell>
                                        <TableCell className="px-5 py-3 font-medium">{status.name}</TableCell>
                                        <TableCell className="px-5 py-3 text-end">
                                            <button
                                                onClick={() => handleDelete(status.id)}
                                                className="text-red-500 hover:text-red-700"
                                                title="Delete"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </Modal>
    );
}
